// ====================================================
// src/components/block/liveStreamManagement/enchainements/MiniTransitionPreview.tsx
//
// Vignette animée montrant COMMENT fonctionne un enchaînement, dans le picker
// de TbarSettingsModal. Ne réimplémente RIEN : elle rejoue `enchainement.render()`
// — le même code que le vrai T-bar — sur un tout petit canvas, avec deux sources
// synthétiques "F" (flux final, bleu) et "P" (preview, vert) à la place des
// vrais flux vidéo. Fidèle par construction : si un enchaînement change de
// comportement, sa vignette suit automatiquement, sans rien à maintenir ici.
// ====================================================

import React, { useEffect, useRef } from 'react';
import { Enchainement, EnchainementDrawRect } from './Enchainement';

// Dimensions par défaut (liste pleine largeur) — la grille de tuiles carrées
// (TbarSettingsModal) passe ses propres width/height, plus compactes.
const DEFAULT_PREVIEW_W = 56;
const DEFAULT_PREVIEW_H = 32;
// Aller-retour continu (0→1→0…) — cosinus plutôt qu'un aller simple linéaire :
// ça ralentit naturellement aux deux bouts (le temps de lire "F" ou "P" plein
// cadre) sans avoir à coder des paliers de pause séparés.
const CYCLE_MS = 2600;

function buildLabelSource(text: string, color: string, w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = 'rgba(255,255,255,0.25)';
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, w - 1, h - 1);
  ctx.fillStyle = '#fff';
  ctx.font = `700 ${Math.round(h * 0.55)}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, w / 2, h / 2 + 1);
  return c;
}

export const MiniTransitionPreview: React.FC<{
  enchainement: Enchainement<any>; config: unknown;
  width?: number; height?: number;
}> = ({ enchainement, config, width = DEFAULT_PREVIEW_W, height = DEFAULT_PREVIEW_H }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    canvas.width  = width;
    canvas.height = height;

    // "F" bleu (déjà à l'antenne) / "P" vert (même accent que la sélection active
    // dans cette modale) — cf. la charte déjà en place, pas de couleur inventée.
    const fSource = buildLabelSource('F', '#1d4ed8', width, height);
    const pSource = buildLabelSource('P', '#059669', width, height);
    const rect: EnchainementDrawRect = { x: 0, y: 0, w: width, h: height };

    let rafId = 0;
    let cancelled = false;
    const start = performance.now();

    const tick = (now: number) => {
      if (cancelled) return;
      const t = ((now - start) % CYCLE_MS) / CYCLE_MS;
      const progress = (1 - Math.cos(2 * Math.PI * t)) / 2;

      ctx.save();
      ctx.clearRect(0, 0, width, height);
      enchainement.render(
        { ctx, currentSource: fSource, currentRect: rect, nextSource: pSource, nextRect: rect, progress, width, height },
        config,
      );
      ctx.restore();

      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => { cancelled = true; cancelAnimationFrame(rafId); };
  }, [enchainement, config, width, height]);

  return (
    <canvas
      ref={canvasRef}
      title={`Aperçu : ${enchainement.label}`}
      style={{
        width, height, flexShrink: 0,
        borderRadius: 5, border: '1px solid rgba(255,255,255,0.15)',
        background: '#0f172a',
      }}
    />
  );
};
